<script setup>
import {
  ComboboxAnchor,
  ComboboxCancel,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxRoot,
  ComboboxViewport,
} from "reka-ui";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from "vue";

import { useAsyncSearch } from "@/composables/useAsyncSearch";

import BaseSpinner from "./BaseSpinner.vue";
import FormField from "./FormField.vue";
import SvgIcon from "./SvgIcon.vue";

// Two modes:
//   • Legacy (`:items`)  — caller supplies a local array; SearchSelect filters it.
//     Use `external-filter` if the caller filters server-side / on its own.
//   • Async  (`:search`) — caller supplies a fetcher; SearchSelect owns the
//     lifecycle. When set, `items` / `loading` / `external-filter` are ignored.
const props = defineProps({
  // Legacy-mode option list (ignored in async mode).
  items: { type: Array, default: () => [] },
  // Item key used for client-side filtering and default rendering.
  displayKey: { type: String, default: "name" },
  placeholder: { type: String, default: "Search..." },
  disabled: { type: Boolean, default: false },
  // Cap on rendered results; 0 = unlimited.
  maxResults: { type: Number, default: 0 },
  // Caller already filtered `items` (e.g. server-side) — skip client-side filtering.
  externalFilter: { type: Boolean, default: false },
  // Legacy-mode loading flag (async mode derives its own).
  loading: { type: Boolean, default: false },
  autofocus: { type: Boolean, default: false },
  label: { type: String, default: "" },
  error: { type: String, default: "" },
  info: { type: String, default: "" },
  // Async mode: `(query) => Promise<item[]>`. When set, SearchSelect owns the
  // search lifecycle (via useAsyncSearch) — debounce, loading, race-guarding, and
  // the initial fetch on mount — and ignores `items` / `loading` / `external-filter`.
  search: { type: Function, default: null },
  creatable: { type: Boolean, default: false },
  createLabel: { type: String, default: "Create" },
  emptyLabel: { type: String, default: "No results found" },
  canCreate: { type: Function, default: null },
  // Caps the typed/pasted query length; null = uncapped.
  maxlength: { type: Number, default: null },
});
// NOTE: the combobox emits an initial empty search value on mount → a phantom `search-change("")`.
// It is NOT load-bearing: the server consumers (EditArt/EditSolutionTrain admins,
// EditTeam members) already load in mounted(), so this just fires a redundant second
// fetch; the local external-filter consumers only use the query to client-filter.
// Safe to drop once those consumers move to `:search`. (Prefer `:search` for new code.)
//
// TODO: planned API cleanup — collapse to two orthogonal props (`:items` for local
// data, `:search` for async fetch) and DELETE `external-filter` + `search-change` +
// this mount-emit quirk. Spec + per-consumer migration:
// docs/superpowers/specs/2026-06-01-searchselect-two-prop-api.md
const emit = defineEmits(["select", "create", "enter", "search-change", "close"]);

const inputRef = useTemplateRef("input");

const isAsync = computed(() => typeof props.search === "function");

// --- Async search lifecycle (active only when `search` is provided) ---
// The fetcher closes over props.search; run/debouncedRun are only invoked when isAsync.
const {
  items: asyncItems,
  loading: asyncLoading,
  hasSearched,
  run: runSearch,
  debouncedRun,
  cancel: cancelSearch,
} = useAsyncSearch((query) => props.search(query));

function focus() {
  const inputEl = inputRef.value?.$el?.querySelector?.("input") ?? inputRef.value?.$el;
  inputEl?.focus?.();
}
// Let callers refocus the field imperatively (e.g. after selecting, to chain entries).
defineExpose({ focus });

onMounted(async () => {
  if (isAsync.value) {
    // Immediate (non-debounced) initial fetch so the spinner shows at once and
    // the list loads on open — a single fetch owned by this component.
    runSearch("");
  }
  if (props.autofocus) {
    await nextTick();
    focus();
  }
});

onBeforeUnmount(() => {
  cancelSearch();
});

function onOpenChange(value) {
  const wasOpen = open.value;
  open.value = value;
  if (wasOpen && !value) {
    emit("close");
  }
}

const searchTerm = ref("");
const hasEmittedSearchTerm = ref(false);
const open = ref(false);

// In async mode, query changes (typing) drive a debounced fetch. The initial
// mount value ("") is handled by the immediate fetch in onMounted, so this only
// reacts to real edits.
watch(searchTerm, (query) => {
  if (isAsync.value) debouncedRun(query);
});

// Async results are server-filtered; otherwise we filter the caller's items.
const sourceItems = computed(() => (isAsync.value ? asyncItems.value : props.items));

const showLoading = computed(() => (isAsync.value ? asyncLoading.value : props.loading));

// The empty message must not show in the async "not searched yet" state.
const showEmptyMessage = computed(() => !isAsync.value || hasSearched.value);

const filtered = computed(() => {
  // Index against the full source first, so the index we hand to ComboboxItem
  // still maps back to sourceItems in onSelect after any filtering below.
  const indexed = sourceItems.value.map((item, index) => ({ item, index }));
  if (isAsync.value || props.externalFilter) {
    return props.maxResults ? indexed.slice(0, props.maxResults) : indexed;
  }
  const q = searchTerm.value?.trim().toLowerCase();
  const list = q
    ? indexed.filter(({ item }) => String(item[props.displayKey]).toLowerCase().includes(q))
    : indexed;
  return props.maxResults ? list.slice(0, props.maxResults) : list;
});

const createQuery = computed(() => searchTerm.value.trim());

const showCreateOption = computed(
  () =>
    props.creatable &&
    createQuery.value &&
    !showLoading.value &&
    showEmptyMessage.value &&
    filtered.value.length === 0 &&
    (!props.canCreate || props.canCreate(createQuery.value)),
);

function onSelect(event) {
  const selectedValue = event.detail.value;
  const idx = Number(selectedValue);
  const item = sourceItems.value[idx];
  if (item) {
    emit("select", item);
  }
  searchTerm.value = "";
}

function onCreateSelect(event) {
  event?.preventDefault?.();
  emit("create", createQuery.value);
  searchTerm.value = "";
}

function onSearchTermChange(value) {
  if (hasEmittedSearchTerm.value && value === searchTerm.value) return;
  searchTerm.value = value;
  hasEmittedSearchTerm.value = true;
  emit("search-change", value);
}

function onKeydownEnter(event) {
  const query = searchTerm.value.trim();
  if (!query) return;

  if (showCreateOption.value) {
    event.preventDefault();
    emit("create", query);
  } else {
    emit("enter", query);
  }
  searchTerm.value = "";
}

// Reka's default filter matches each item's `value` (the index string we set) against the
// search term, which would hide every item. Our own filtering lives in `filtered` above, so
// disable Reka's built-in filter.
</script>

<template>
  <FormField :label :error :info>
    <template v-if="$slots.label" #label>
      <slot name="label" />
    </template>
    <template v-if="$slots['label-info']" #label-info="{ id: descriptionId }">
      <slot :id="descriptionId" name="label-info" />
    </template>
    <template #default="{ describedBy }">
      <div class="search-select">
        <ComboboxRoot
          :open
          :disabled
          :ignore-filter="true"
          :reset-search-term-on-select="true"
          @update:open="onOpenChange"
        >
          <ComboboxAnchor class="anchor">
            <slot name="leading" />
            <SvgIcon name="system/search" width="16" height="16" class="search-icon" />
            <ComboboxInput
              ref="input"
              :model-value="searchTerm"
              :display-value="() => ''"
              :placeholder
              :maxlength
              class="search-input"
              :aria-describedby="describedBy"
              @update:model-value="onSearchTermChange"
              @focus="open = true"
              @keydown.enter="onKeydownEnter"
            />
            <ComboboxCancel v-if="searchTerm" class="clear-btn" aria-label="Clear search">
              &times;
            </ComboboxCancel>
          </ComboboxAnchor>

          <ComboboxContent
            class="content"
            position="popper"
            align="start"
            :side-offset="4"
            :body-lock="false"
          >
            <ComboboxViewport class="viewport">
              <div v-if="showLoading" class="loading">
                <BaseSpinner />
              </div>
              <template v-else>
                <div v-if="showEmptyMessage && filtered.length === 0" class="no-results">
                  <slot name="empty" :query="createQuery">{{ emptyLabel }}</slot>
                </div>
                <ComboboxItem
                  v-if="showCreateOption"
                  value="__create__"
                  class="item create-item"
                  @select="onCreateSelect"
                >
                  <slot name="create" :query="createQuery">{{ createLabel }}</slot>
                </ComboboxItem>
                <ComboboxItem
                  v-for="{ item, index } in filtered"
                  :key="index"
                  :value="String(index)"
                  class="item"
                  @select="onSelect"
                >
                  <slot name="item" :item>{{ item[displayKey] }}</slot>
                </ComboboxItem>
              </template>
            </ComboboxViewport>
          </ComboboxContent>
        </ComboboxRoot>
      </div>
    </template>
  </FormField>
</template>

<style lang="scss" scoped>
@use "@/colors" as c;

.search-select {
  position: relative;
  margin-bottom: 0.5rem;
}

.anchor {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 10px;
  color: c.$text-subtle;
  pointer-events: none;
  z-index: 1;
}

.search-input {
  width: 100%;
  height: 36px;
  padding: 0 2rem 0 2.25rem;
  font-family: inherit;
  font-size: 0.8125rem;
  color: c.$text;
  background-color: c.$background-ui;
  border: 1px solid c.$border;
  border-radius: 6px;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: c.$text-placeholder;
  }

  &:hover:not(:focus, :disabled) {
    border-color: c.$text-subtle;
  }

  &:focus {
    border-color: c.$main;
  }

  &:disabled {
    background-color: c.$grey-lightest;
    cursor: not-allowed;
  }
}

.clear-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: none;
  border: none;
  border-radius: 2px;
  cursor: pointer;
  color: c.$text-subtle;
  font-size: 1.125rem;
  line-height: 1;

  &:hover {
    color: c.$text;
  }
}
</style>

<style lang="scss">
@use "@/colors" as c;

/* Unscoped — required because ComboboxContent renders in a portal outside this component */
.search-select .content {
  width: var(--reka-combobox-trigger-width);
  background: c.$white;
  border-radius: 8px;
  box-shadow: 0 2px 16px rgb(5 0 56 / 12%);
  z-index: 50;
  overflow: hidden;
}

.search-select .viewport {
  padding: 4px;
  max-height: 320px;
  overflow-y: auto;
}

.search-select .item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 36px;
  padding: 0 12px;
  font-size: 0.8125rem;
  color: c.$text;
  cursor: pointer;
  border-radius: 4px;
  outline: none;
  user-select: none;

  &[data-highlighted] {
    background-color: c.$background-grey-light;
  }

  &[data-disabled] {
    color: c.$text-disabled;
    cursor: not-allowed;
    pointer-events: none;
  }
}

.search-select .create-item {
  color: c.$main;

  &[data-highlighted] {
    background-color: c.$main-inverse;
  }
}

.search-select .no-results {
  padding: 8px 12px 4px;
  font-size: 0.8125rem;
  color: c.$text-subtle;
}

.search-select .loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  font-size: 0.8125rem;
  color: c.$text-subtle;
}
</style>
